import { CustomerPricing, ICustomerPricing } from '../customerpricing';

import {
  ICustomer,
  ICustomerData,
  ICustomerAddress,
  ICustomerAddressData
} from './customer.types';

export class CustomerAddress implements ICustomerAddress {
  readonly id?: number;
  readonly customer: string;
  readonly name: string;
  readonly address1: string;
  readonly address2: string;
  readonly address3: string;
  readonly address4: string;
  readonly city: string;
  readonly region: string;
  readonly postal_code: string;
  readonly country: string;

  constructor(data: ICustomerAddressData) {
    if (data.id) {
      this.id = data.id;
    }

    this.country = data.country;
    this.customer = data.customer;

    this.name = data.name ?? '';
    this.address1 = data.address1 ?? '';
    this.address2 = data.address2 ?? '';
    this.address3 = data.address3 ?? '';
    this.address4 = data.address4 ?? '';
    this.city = data.city ?? '';
    this.region = data.region ?? '';
    this.postal_code = data.postal_code ?? '';
  }

  validate(): boolean {
    if (!this.country || !this.customer) {
      return false;
    }
    return true;
  }
}

export class Customer implements ICustomer {
  id?: string;
  name = '';
  address1 = '';
  address2 = '';
  address3 = '';
  address4 = '';
  city = '';
  region = '';
  postal_code = '';
  country = '';
  eu_code = '';
  language = '';
  currency = '';
  type = '';
  type_description = '';
  tax_id = '';
  terms = '';
  shipping_addresses: ICustomerAddress[] = [];
  contract: null | ICustomerPricing = null;

  constructor(data?: ICustomerData) {
    if (data) {
      if (data.id) {
        this.id = data.id;
      }
      if (data.name) {
        this.name = data.name;
      }
      if (data.address1) {
        this.address1 = data.address1;
      }
      if (data.address2) {
        this.address2 = data.address2;
      }
      if (data.address3) {
        this.address3 = data.address3;
      }
      if (data.address4) {
        this.address4 = data.address4;
      }
      if (data.city) {
        this.city = data.city;
      }
      if (data.region) {
        this.region = data.region;
      }
      if (data.postal_code) {
        this.postal_code = data.postal_code;
      }
      if (data.country) {
        this.country = data.country;
      }
      if (data.eu_code) {
        this.eu_code = data.eu_code;
      }
      if (data.language) {
        this.language = data.language;
      }
      if (data.currency) {
        this.currency = data.currency;
      }
      if (data.type) {
        this.type = data.type;
      }
      if (data.type_description) {
        this.type_description = data.type_description;
      }
      if (data.terms) {
        this.terms = data.terms;
      }
      if (data.tax_id) {
        this.tax_id = data.tax_id;
      }

      this.shipping_addresses = (data.shipping_addresses ?? []).map(
        shippingAddress => new CustomerAddress(shippingAddress)
      );

      if (data.contract) {
        this.contract = new CustomerPricing(data.contract);
      }
    }
  }

  validate(): boolean {
    if (!this.name) {
      return false;
    }
    return true;
  }

  getAddressString() {
    const addresses: string[] = [];

    if (this.address1 || this.address2) {
      const firstAddressGroup = [];
      if (this.address1) firstAddressGroup.push(this.address1);
      if (this.address2) firstAddressGroup.push(this.address2);

      addresses.push(firstAddressGroup.join(', '));
    }

    if (this.address3 || this.address4) {
      const secondAddressGroup = [];
      if (this.address3) secondAddressGroup.push(this.address3);
      if (this.address4) secondAddressGroup.push(this.address4);

      addresses.push(secondAddressGroup.join(', '));
    }

    if (this.city || this.region) {
      const cityAddressGroup = [];
      if (this.city) cityAddressGroup.push(this.city);
      if (this.region) cityAddressGroup.push(this.region);

      addresses.push(cityAddressGroup.join(' '));
    }

    if (this.postal_code || this.country) {
      const countryAddressGroup = [];

      if (this.country) countryAddressGroup.push(this.country);
      if (this.postal_code) countryAddressGroup.push(this.postal_code);
      addresses.push(countryAddressGroup.join(' '));
    }

    return addresses.join(', \n');
  }
}
